/**
 * Pen, highlighter, eraser, select, shapes, text, sticky notes or the laser (SRS C4).
 *
 * docs/06-UI-SPEC.md §6.2 puts these in the left tool rail, which is built later in
 * Phase 4. Until then they live at the top of the properties panel, so the teacher can
 * still reach every tool that exists.
 */

import type { ReactElement } from 'react';

import type { ToolId } from '@opal/shared';

import {
  EraserIcon,
  HighlighterIcon,
  LaserIcon,
  PenIcon,
  SelectIcon,
  ShapesIcon,
  StickyIcon,
  TextIcon,
  type IconProps,
} from '../../assets/icons.js';
import { useBoardStore } from '../../board/store.js';

interface ToolChoice {
  readonly value: ToolId;
  readonly label: string;
  readonly Icon: (props: IconProps) => ReactElement;
}

const TOOLS: readonly ToolChoice[] = [
  { value: 'pen', label: 'Pen', Icon: PenIcon },
  { value: 'highlighter', label: 'Highlighter', Icon: HighlighterIcon },
  { value: 'eraser', label: 'Eraser', Icon: EraserIcon },
  { value: 'select', label: 'Select', Icon: SelectIcon },
  { value: 'shape', label: 'Shapes', Icon: ShapesIcon },
  { value: 'text', label: 'Text', Icon: TextIcon },
  { value: 'sticky', label: 'Sticky', Icon: StickyIcon },
  { value: 'laser', label: 'Laser', Icon: LaserIcon },
];

export function ToolPicker() {
  const tool = useBoardStore((state) => state.tool);
  const setTool = useBoardStore((state) => state.setTool);

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-text-secondary">Tool</h3>
      <div className="grid grid-cols-3 gap-2">
        {TOOLS.map(({ value, label, Icon }) => {
          const active = tool === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setTool(value);
              }}
              className={`flex min-h-touch flex-col items-center justify-center gap-1 rounded-md border px-1 py-2 text-xs transition-colors duration-fast ${
                active
                  ? 'border-primary bg-primary-subtle text-primary'
                  : 'border-border bg-surface text-text-primary hover:bg-surface-hover'
              }`}
            >
              <Icon size={20} />
              {label}
            </button>
          );
        })}
      </div>
    </section>
  );
}
